import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Container, VStack, Heading, Text, Box, Badge, HStack, Image,
  OrderedList, ListItem, Divider, Button, Link,
} from "@chakra-ui/react";
import { FiArrowLeft, FiArrowRight } from "react-icons/fi";
import { NavbarWithCallToAction } from "../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";

const ModuleTransitionsForSurgReady: React.FC = () => {
  return (
    <>
      <NavbarWithCallToAction />
      <Container maxW="container.md" py={10}>
        <VStack spacing={6} align="stretch">
          <Link as={RouterLink} to="/updates" _hover={{ textDecoration: "none" }}>
            <Button variant="ghost" size="sm" leftIcon={<FiArrowLeft />} color="gray.500">
              All Updates
            </Button>
          </Link>

          <VStack spacing={3} align="start">
            <HStack>
              <Badge colorScheme="blue">SurgReady</Badge>
              <Badge colorScheme="green">Learning Modules</Badge>
              <Text fontSize="xs" color="gray.400">20 Apr 2026</Text>
            </HStack>
            <Heading size="lg" color="#2c5282">
              Students now get a moment between modules
            </Heading>
            <Text color="gray.600">
              A small polish: when a student finishes all the lessons in a module and clicks Next Lesson, they now see a short celebration card and a confetti burst welcoming them into the next module. Small thing, big feel.
            </Text>
          </VStack>

          <Box>
            <Image
              src="/images/module-transition-celebration.png"
              alt="Module Complete celebration — Surgery and You module done, welcome to Nutrition, with a confetti burst across the screen."
              borderRadius="md"
              boxShadow="0 4px 16px rgba(0,0,0,0.1)"
              border="1px solid"
              borderColor="gray.200"
            />
          </Box>

          <Divider />

          <Box>
            <Heading size="md" color="#2b6cb0" mb={3}>What's New</Heading>
            <VStack spacing={3} align="start">
              <Text>
                The SurgReady learning course has twelve modules. Before this update, finishing one module and starting the next happened quietly — the student just saw a Next Lesson link and moved on. There was no visual signal that they'd just crossed a meaningful milestone.
              </Text>
              <Text>
                Now, when a student completes a module and clicks Next Lesson, a celebration card appears with a ✓ <strong>Module complete</strong> badge, the title of the module they just finished, a welcome for the next module, and a short burst of confetti. The card fades after a few seconds. The student can also click <strong>Continue</strong> to move on immediately.
              </Text>
              <Text>
                Finishing the whole course gets a similar treatment — a <em>you've finished the course</em> moment instead of the old quiet redirect.
              </Text>
            </VStack>
          </Box>

          <Box>
            <Heading size="md" color="#2b6cb0" mb={3}>How It Works</Heading>
            <OrderedList spacing={3} pl={4}>
              <ListItem>Student opens a lesson that's the last one in a module.</ListItem>
              <ListItem>They go through the lesson content and click <strong>Mark Complete</strong>.</ListItem>
              <ListItem>They click <strong>Next Lesson</strong>.</ListItem>
              <ListItem>A celebration card pops up — module they just completed, module they're about to start, short confetti burst.</ListItem>
              <ListItem>The card auto-dismisses after a few seconds, or they can click Continue to skip ahead.</ListItem>
            </OrderedList>
          </Box>

          <Box>
            <Heading size="md" color="#2b6cb0" mb={3}>Why It Matters</Heading>
            <Text>
              Module transitions now feel distinct. Students get a subtle reinforcement that they're making progress, which tends to improve completion rates across self-paced courses. It also works on mobile — most SurgReady patients are preparing for surgery on their phones rather than at a desk.
            </Text>
          </Box>

          <Box>
            <Heading size="md" color="#2b6cb0" mb={3}>A Note on the Platform</Heading>
            <Text>
              SurgReady runs on the Business Builder System — the multi-tenant platform Tom Miller Services builds and operates. As the platform evolves, improvements like this one flow through to every tenant, SurgReady included. SurgReady is currently on the Foundation plan.
            </Text>
          </Box>

          <Divider />

          <Box bg="green.50" p={5} borderRadius="md" border="1px solid" borderColor="green.200">
            <Heading size="md" color="#38a169" mb={3}>Thank You, Ronan</Heading>
            <Text mb={3}>
              This one came straight off Ronan's feedback on WhatsApp — spotted the gap, suggested a title card or confetti, and here it is. Ops eyes on the product pay for themselves every time.
            </Text>
          </Box>

          <Box>
            <Text>
              When you get a moment, it'd be worth confirming a card on file on the tenant billing page — keeps things clean as more students sign up. No rush, just housekeeping.
            </Text>
            <Text mt={4}>
              As always, reach out if you have any questions, requests, or new ideas.
            </Text>
            <Text mt={4}>
              Regards,<br />
              <strong>Dr Thomas Miller</strong><br />
              <Text as="em" color="gray.500">Tom Miller Services · Business Builder System</Text>
            </Text>
          </Box>
        </VStack>
      </Container>
      <FooterWithFourColumns />
    </>
  );
};

export default ModuleTransitionsForSurgReady;
